import { DataRow, DataTable, EffectiveDefinition } from '../../models';
import { accessorForRefOrValue } from '../../models/Accessor';
import { Calculation, ColumnRefOrValue } from '../../models/EffectiveDefinition';
import { TransformCreator, TransformFunction } from '../transform';

export const SimplePredicateType = 'simple-predicate';

/**
 * This Calculation type compares the `lhs` and `rhs` with the given `operator`,
 * and returns the `boolean` result of the comparison.
 *
 * Currently has no epsilon value, so float equality might get weird.
 */
export interface SimplePredicateCalculation {
  readonly type: typeof SimplePredicateType;
  readonly lhs: ColumnRefOrValue;
  readonly rhs: ColumnRefOrValue;
  readonly operator: ComparisonOperator;
}

export enum ComparisonOperator {
  EQUAL = 'equal',
  GREATER_THAN = 'greater-than',
  GREATER_THAN_OR_EQUAL = 'greater-than-or-equal',
  LESS_THAN = 'less-than',
  LESS_THAN_OR_EQUAL = 'less-than-or-equal',
  NOT_EQUAL = 'not-equal',
}

export type ComparisonOperation = (lhs: number | string, rhs: number | string) => boolean;

const operatorFunc = (operator: ComparisonOperator): ComparisonOperation => {
  switch (operator) {
    case ComparisonOperator.EQUAL:
      return (lhs, rhs) => lhs === rhs;
    case ComparisonOperator.GREATER_THAN:
      return (lhs, rhs) => lhs > rhs;
    case ComparisonOperator.GREATER_THAN_OR_EQUAL:
      return (lhs, rhs) => lhs >= rhs;
    case ComparisonOperator.LESS_THAN:
      return (lhs, rhs) => lhs < rhs;
    case ComparisonOperator.LESS_THAN_OR_EQUAL:
      return (lhs, rhs) => lhs <= rhs;
    case ComparisonOperator.NOT_EQUAL:
      return (lhs, rhs) => lhs !== rhs;
    default:
      return (lhs, rhs) => false;
  }
};

export const isSimplePredicateCalculation = (calc?: Calculation): calc is SimplePredicateCalculation => {
  return calc !== undefined && calc.type === SimplePredicateType;
};

export const simplePredicateTransformCreator: TransformCreator = (
  definition: EffectiveDefinition,
  name: string
): TransformFunction => {
  const column = definition.schema[name];
  if (column.calculation === undefined) {
    throw new Error(`Could not find calculated column with name: ${name}`);
  }
  const calc = column.calculation;
  if (!isSimplePredicateCalculation(calc)) {
    throw new Error(`Could not find simple predicate column with name: ${name}`);
  }
  return (sourceTable: DataTable, windowedTable: DataTable) => {
    return windowedTable.map((row) => {
      const result = applySimplePredicate(calc, row);
      return { ...row, [name]: result };
    });
  };
};

export const applySimplePredicate = (calc: SimplePredicateCalculation, row: DataRow): boolean => {
  const lhsAccessor = accessorForRefOrValue(calc.lhs);
  const rhsAccessor = accessorForRefOrValue(calc.rhs);
  const op = operatorFunc(calc.operator);
  return op(lhsAccessor(row), rhsAccessor(row));
};
